import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus, MessageSquare, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

import {
  TechLabel,
  StatusBadge,
  PageContainer,
  PrimaryButton,
  motionFadeUp,
  C,
} from "../../design-system";
import { createPageUrl } from "../../utils";

/* ------------------------------------------------------------------ */
/*  CONTENT                                                            */
/* ------------------------------------------------------------------ */
const groups = [
  {
    label: "Platform",
    items: [
      {
        q: "What is WebIdeal exactly?",
        a: "An AI-orchestrated hiring platform: ATS, recruitment CRM, HRMS and a job portal that share one record. Pay for one suite, run every team's workflow on the same data.",
      },
      {
        q: "Do I have to use all four products?",
        a: "No. Each module works standalone, but the value compounds when you use them together, the same candidate, the same fit score, the same audit trail.",
      },
      {
        q: "How long does setup take?",
        a: "Starter teams are live in under an hour. Enterprise rollouts (SSO, custom workflows, multi-country) usually run two to four weeks with our customer success team.",
      },
    ],
  },
  {
    label: "AI",
    items: [
      {
        q: "Where does the AI run?",
        a: "Our managed cloud, in the region you select. Customer data isn't sent to public model providers without explicit configuration on your side.",
      },
      {
        q: "Will my data train shared models?",
        a: "Never by default. We only fine-tune private models on your data with explicit opt-in, and that data stays scoped to your tenant.",
      },
      {
        q: "How accurate is the fit scoring?",
        a: "Average match accuracy across customers is 85%+ on roles with sufficient historical data. We always show the inputs the model used so you can audit decisions.",
      },
    ],
  },
  {
    label: "Pricing & billing",
    items: [
      {
        q: "Is there a free trial?",
        a: "Yes, 14 days, no credit card. We send a friendly reminder before any charge, never an autobill ambush.",
      },
      {
        q: "Can I switch plans later?",
        a: "Anytime. Upgrades pro-rate immediately, downgrades take effect at the next billing cycle.",
      },
      {
        q: "Do you offer non-profit pricing?",
        a: "30% off Starter and Pro for verified non-profits and educational institutions.",
      },
    ],
  },
  {
    label: "Security & compliance",
    items: [
      {
        q: "Are you SOC 2 / GDPR compliant?",
        a: "SOC 2 Type II completed. GDPR-ready out of the box with EU-region hosting available. ISO 27001 in progress.",
      },
      {
        q: "How is my data encrypted?",
        a: "Encryption at rest (AES-256) and in transit (TLS 1.3). Customer-managed keys available on Enterprise.",
      },
      {
        q: "Do you do penetration testing?",
        a: "Yes, annually with a third party. Reports are available to Enterprise customers under NDA.",
      },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  PAGE                                                               */
/* ------------------------------------------------------------------ */
export default function FAQ() {
  const [activeGroup, setActiveGroup] = useState(groups[0].label);
  const current = groups.find((g) => g.label === activeGroup) || groups[0];

  return (
    <div
      className="relative"
      style={{
        background: C.paper,
        color: C.forest,
        fontFamily: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
      }}
    >
      {/* HERO */}
      <section
        className="relative pt-16 pb-16 md:pt-24 md:pb-20 border-b ds-bg-spotlight"
        style={{ borderColor: C.hairline }}
      >
        <PageContainer>
          <motion.div initial="hidden" animate="show" variants={motionFadeUp}>
            <motion.div variants={motionFadeUp} custom={0}>
              <StatusBadge>FAQ · 12 answers</StatusBadge>
            </motion.div>
            <motion.h1
              variants={motionFadeUp}
              custom={1}
              className="mt-5 text-5xl sm:text-6xl lg:text-[5rem] font-extrabold leading-[0.92] tracking-tight max-w-4xl"
              style={{ color: C.forest }}
            >
              Quick answers.
              <br />
              <span style={{ color: `${C.forest}80` }}>No fluff.</span>
            </motion.h1>
            <motion.p
              variants={motionFadeUp}
              custom={2}
              className="mt-6 text-lg md:text-xl leading-relaxed max-w-2xl"
              style={{ color: C.grid }}
            >
              The questions we answer most often. If yours isn't here, the contact
              page goes straight to a human.
            </motion.p>
          </motion.div>
        </PageContainer>
      </section>

      {/* TAB NAV + ACCORDION */}
      <section className="py-16 md:py-20 border-b" style={{ borderColor: C.hairline }}>
        <PageContainer>
          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-px mb-10" style={{ background: C.hairline }}>
            {groups.map((g, i) => {
              const active = g.label === activeGroup;
              return (
                <button
                  key={g.label}
                  type="button"
                  onClick={() => setActiveGroup(g.label)}
                  className="cursor-pointer px-4 md:px-5 py-3 text-xs font-bold uppercase transition-colors duration-150 inline-flex items-center gap-2"
                  style={{
                    letterSpacing: "0.18em",
                    background: active ? C.forest : C.paper,
                    color: active ? C.paper : C.grid,
                  }}
                >
                  <span
                    className="w-1.5 h-1.5"
                    style={{ background: active ? C.mint : `${C.grid}77` }}
                  />
                  {g.label}
                </button>
              );
            })}
          </div>

          {/* Accordion */}
          <div className="border" style={{ borderColor: C.hairline, background: C.paper }}>
            {current.items.map((item, idx) => (
              <FAQItem
                key={`${current.label}-${idx}`}
                index={idx}
                last={idx === current.items.length - 1}
                question={item.q}
                answer={item.a}
              />
            ))}
          </div>
        </PageContainer>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24" style={{ background: C.forest, color: C.paper }}>
        <PageContainer>
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="max-w-xl">
              <StatusBadge color={C.mint}>Still curious?</StatusBadge>
              <h2 className="mt-4 text-3xl md:text-4xl font-bold leading-[1.05] tracking-tight">
                Talk to a human.
              </h2>
              <p className="mt-3 text-base" style={{ color: "rgba(247,247,245,0.7)" }}>
                We'd rather have a 20-minute call than write a longer FAQ.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Link to={createPageUrl("Contact")}>
                <PrimaryButton>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Book a demo
                </PrimaryButton>
              </Link>
              <a
                href="mailto:Info@webidealglobal.com"
                className="cursor-pointer h-12 px-6 text-sm font-semibold border bg-transparent inline-flex items-center hover:bg-[#9EFFBF] hover:text-[#1A3C2B] transition-colors duration-150"
                style={{ borderColor: "rgba(158,255,191,0.4)", color: C.paper }}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Email us
              </a>
            </div>
          </div>
        </PageContainer>
      </section>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  ACCORDION ITEM                                                     */
/* ------------------------------------------------------------------ */
function FAQItem({ index, last, question, answer }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div
      className="transition-colors duration-200"
      style={{
        borderBottom: last ? "none" : `1px solid ${C.hairline}`,
        background: open ? "#FFFFFF" : "transparent",
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="cursor-pointer w-full flex items-start justify-between gap-4 p-5 md:p-6 text-left"
      >
        <div className="flex items-start gap-4 min-w-0">
          <TechLabel className="mt-1.5 shrink-0" style={{ color: `${C.grid}99` }}>
            0{index + 1}
          </TechLabel>
          <h3
            className="text-base md:text-lg font-bold leading-snug min-w-0"
            style={{ color: C.forest }}
          >
            {question}
          </h3>
        </div>
        <span
          className="w-8 h-8 flex-shrink-0 flex items-center justify-center border transition-colors duration-200"
          style={{
            borderColor: open ? C.forest : C.hairline,
            background: open ? C.forest : "transparent",
            color: open ? C.paper : C.grid,
          }}
        >
          {open ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
        </span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.25, 0.1, 0.25, 1] }}
            className="overflow-hidden"
          >
            <div className="px-5 md:px-6 pb-6 pl-[3.5rem] md:pl-[4rem]">
              <p className="text-sm md:text-base leading-relaxed" style={{ color: C.grid }}>
                {answer}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
